import { Feature } from '@/config/features'
import { useHasFeature } from '@/hooks/useHasFeature'
import { useOptionalUser } from '@/pages/user/selectors'

const INTERNAL_EMAIL_DOMAINS = ['@dupr.com', '@mydupr.com']

/**
 * Whether the given email belongs to an internal DUPR account. Internal staff
 * always get the incubating capability tooling so they can dogfood it.
 */
export const isInternalDuprEmail = (email: string | null | undefined): boolean => {
  if (!email) return false
  const normalized = email.trim().toLowerCase()
  return INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain))
}

/**
 * Client-side gate for the (data-driven) club capability tooling. Internal DUPR
 * staff always see it (for dogfooding); the remote-config flag controls general
 * availability for everyone else. The server is the final gate either way — it
 * only returns a capability schema for clubs that actually hold the capability
 * and only to viewers with the view permission, so the UI renders nothing on a
 * non-enabled club or for a non-manager regardless of this hook. Kept
 * deliberately generic so the underlying capability is not obvious from code.
 */
export const useCanUseClubCapabilities = (): boolean => {
  const generallyAvailable = useHasFeature(Feature.ClubCapabilities)
  const user = useOptionalUser()
  return isInternalDuprEmail(user?.email) || generallyAvailable
}
